import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getSalesReturns = createAsyncThunk(
  "salesReturn/getSalesReturns",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.SALES_RETURN.SLUG}?${queryString}`
      );
      const sales_return_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { sales_return_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getSalesReturnById = createAsyncThunk(
  "salesReturn/getSalesReturnById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.SALES_RETURN.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getNextRmaNumber = createAsyncThunk(
  "salesReturn/getNextRmaNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.SALES_RETURN.GET_NEXT_RMA_NUMBER);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getSalesReturnItems = createAsyncThunk(
  "salesReturn/getSalesReturnItems",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.SALES_RETURN.GET_ITEMS(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const approveSalesReturn = createAsyncThunk(
  "salesReturn/approveSalesReturn",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.SALES_RETURN.APPROVE(id));
      // Refresh the detail after approval
      dispatch(getSalesReturnById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getSalesOrdersForReturn = createAsyncThunk(
  "salesReturn/getSalesOrdersForReturn",
  async (payload, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (payload.searchText) params.append("searchText", payload.searchText);
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.SALES_ORDER.RETURN_ELIGIBLE}${queryString ? `?${queryString}` : ""}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getEligibleInvoices = createAsyncThunk(
  "salesReturn/getEligibleInvoices",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SALES_RETURN.ELIGIBLE_INVOICES(salesOrderId)
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const prepareSalesReturn = createAsyncThunk(
  "salesReturn/prepareSalesReturn",
  async ({ salesOrderId, invoiceId, excludeSalesReturnId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (salesOrderId) params.append("salesOrderId", salesOrderId);
      if (invoiceId) params.append("invoiceId", invoiceId);
      if (excludeSalesReturnId) params.append("excludeSalesReturnId", excludeSalesReturnId);
      const { data } = await axios.get(
        `${API_URL.SALES_RETURN.PREPARE}?${params.toString()}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createSalesReturn = createAsyncThunk(
  "salesReturn/createSalesReturn",
  async (payload, { rejectWithValue }) => {
    try {
      // Build FormData for multipart/form-data submission
      // API expects: SalesOrderId, SalesOrderInvoiceId, ReturnType, RMANumber, Remarks
      // Items: SalesOrderInvoiceItemId, ReturnQty, ReasonType, ReasonOther
      const formData = new FormData();

      // Legacy flag
      formData.append("IsLegacy", payload.isLegacy ? "true" : "false");
      formData.append("IsPriceTaxInclusive", payload.isPriceTaxInclusive ? "true" : "false");

      if (payload.isLegacy) {
        // Legacy-specific header fields
        if (payload.legacyInvoiceNumber) formData.append("LegacyInvoiceNumber", payload.legacyInvoiceNumber);
        if (payload.legacyInvoiceDate) formData.append("LegacyInvoiceDate", payload.legacyInvoiceDate);
        if (payload.customerId) formData.append("CustomerId", payload.customerId);
        if (payload.locationId) formData.append("LocationId", payload.locationId);
        if (payload.warehouseId) formData.append("WarehouseId", payload.warehouseId);
        if (payload.customerGSTNumber) formData.append("CustomerGSTNumber", payload.customerGSTNumber);
        if (payload.billingStateId) formData.append("BillingStateId", payload.billingStateId);
        if (payload.billingAddress) {
          formData.append("BillingAddress.AddressLine1", payload.billingAddress.addressLine1 || "");
          formData.append("BillingAddress.AddressLine2", payload.billingAddress.addressLine2 || "");
          formData.append("BillingAddress.City", payload.billingAddress.city || "");
          if (payload.billingAddress.stateId) formData.append("BillingAddress.StateId", payload.billingAddress.stateId);
          if (payload.billingAddress.stateName) formData.append("BillingAddress.StateName", payload.billingAddress.stateName);
          formData.append("BillingAddress.PinCode", payload.billingAddress.pinCode || "");
        }
        if (payload.shippingAddress) {
          formData.append("ShippingAddress.AddressLine1", payload.shippingAddress.addressLine1 || "");
          formData.append("ShippingAddress.AddressLine2", payload.shippingAddress.addressLine2 || "");
          formData.append("ShippingAddress.City", payload.shippingAddress.city || "");
          if (payload.shippingAddress.stateId) formData.append("ShippingAddress.StateId", payload.shippingAddress.stateId);
          if (payload.shippingAddress.stateName) formData.append("ShippingAddress.StateName", payload.shippingAddress.stateName);
          formData.append("ShippingAddress.PinCode", payload.shippingAddress.pinCode || "");
        }
      } else {
        // System flow fields
        formData.append("SalesOrderId", payload.salesOrderId);
        formData.append("SalesOrderInvoiceId", payload.salesOrderInvoiceId);
      }

      // Common fields
      formData.append("ReturnType", payload.returnType);

      if (payload.rmaNumber) {
        formData.append("RMANumber", payload.rmaNumber);
      }
      if (payload.remarks) {
        formData.append("Remarks", payload.remarks);
      }

      // Add items array (matching SalesReturnItemCreateRequest DTO)
      payload.items.forEach((item, index) => {
        if (!payload.isLegacy) {
          formData.append(`Items[${index}].SalesOrderInvoiceItemId`, item.salesOrderInvoiceItemId);
        }
        formData.append(`Items[${index}].ReturnQty`, item.returnQty);
        formData.append(`Items[${index}].ReasonType`, item.reasonType || 1);
        if (item.reasonOther) {
          formData.append(`Items[${index}].ReasonOther`, item.reasonOther);
        }
        // Legacy item fields
        if (payload.isLegacy) {
          if (item.itemId) formData.append(`Items[${index}].ItemId`, item.itemId);
          if (item.unitPrice != null) formData.append(`Items[${index}].UnitPrice`, item.unitPrice);
          if (item.discountType != null) formData.append(`Items[${index}].DiscountType`, item.discountType);
          if (item.discountValue != null) formData.append(`Items[${index}].DiscountValue`, item.discountValue);
          if (item.discountAmount != null) formData.append(`Items[${index}].DiscountAmount`, item.discountAmount);
          if (item.cgstRate != null) formData.append(`Items[${index}].CGSTRate`, item.cgstRate);
          if (item.sgstRate != null) formData.append(`Items[${index}].SGSTRate`, item.sgstRate);
          if (item.igstRate != null) formData.append(`Items[${index}].IGSTRate`, item.igstRate);
        }
      });

      // Add new documents
      if (payload.documents?.length > 0) {
        payload.documents.forEach((file) => {
          formData.append("Documents", file);
        });
      }

      const { data } = await axios.post(API_URL.SALES_RETURN.SLUG, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return data.data;
    } catch (error) {
      // The axios interceptor rejects with `error.response`, so the API body lives
      // at `error.data`. Pass that to rejectWithValue so validationErrorNotification
      // can read `.isSuccess` / `.message` and surface validation errors (e.g. the
      // "Branch transfer goods cannot be returned" guard) to the user.
      return rejectWithValue(error?.data ?? error);
    }
  }
);

export const updateSalesReturn = createAsyncThunk(
  "salesReturn/updateSalesReturn",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      // Build FormData for multipart/form-data submission
      const formData = new FormData();

      // Add non-array fields (matching SalesReturnUpdateRequest DTO)
      formData.append("SalesReturnId", payload.salesReturnId);
      formData.append("IsLegacy", payload.isLegacy ? "true" : "false");
      formData.append("IsPriceTaxInclusive", payload.isPriceTaxInclusive ? "true" : "false");
      formData.append("ReturnType", payload.returnType);

      if (payload.isLegacy) {
        // Legacy-specific header fields
        if (payload.legacyInvoiceNumber) formData.append("LegacyInvoiceNumber", payload.legacyInvoiceNumber);
        if (payload.legacyInvoiceDate) formData.append("LegacyInvoiceDate", payload.legacyInvoiceDate);
        if (payload.customerId) formData.append("CustomerId", payload.customerId);
        if (payload.locationId) formData.append("LocationId", payload.locationId);
        if (payload.warehouseId) formData.append("WarehouseId", payload.warehouseId);
        if (payload.customerGSTNumber) formData.append("CustomerGSTNumber", payload.customerGSTNumber);
        if (payload.billingStateId) formData.append("BillingStateId", payload.billingStateId);
        if (payload.billingAddress) {
          formData.append("BillingAddress.AddressLine1", payload.billingAddress.addressLine1 || "");
          formData.append("BillingAddress.AddressLine2", payload.billingAddress.addressLine2 || "");
          formData.append("BillingAddress.City", payload.billingAddress.city || "");
          if (payload.billingAddress.stateId) formData.append("BillingAddress.StateId", payload.billingAddress.stateId);
          if (payload.billingAddress.stateName) formData.append("BillingAddress.StateName", payload.billingAddress.stateName);
          formData.append("BillingAddress.PinCode", payload.billingAddress.pinCode || "");
        }
        if (payload.shippingAddress) {
          formData.append("ShippingAddress.AddressLine1", payload.shippingAddress.addressLine1 || "");
          formData.append("ShippingAddress.AddressLine2", payload.shippingAddress.addressLine2 || "");
          formData.append("ShippingAddress.City", payload.shippingAddress.city || "");
          if (payload.shippingAddress.stateId) formData.append("ShippingAddress.StateId", payload.shippingAddress.stateId);
          if (payload.shippingAddress.stateName) formData.append("ShippingAddress.StateName", payload.shippingAddress.stateName);
          formData.append("ShippingAddress.PinCode", payload.shippingAddress.pinCode || "");
        }
      } else {
        // System flow fields
        formData.append("SalesOrderId", payload.salesOrderId);
        formData.append("SalesOrderInvoiceId", payload.salesOrderInvoiceId);
      }

      if (payload.remarks) {
        formData.append("Remarks", payload.remarks);
      }

      // Add items array
      payload.items.forEach((item, index) => {
        if (!payload.isLegacy) {
          formData.append(`Items[${index}].SalesOrderInvoiceItemId`, item.salesOrderInvoiceItemId);
        }
        formData.append(`Items[${index}].ReturnQty`, item.returnQty);
        formData.append(`Items[${index}].ReasonType`, item.reasonType || 1);
        if (item.reasonOther) {
          formData.append(`Items[${index}].ReasonOther`, item.reasonOther);
        }
        // Legacy item fields
        if (payload.isLegacy) {
          if (item.itemId) formData.append(`Items[${index}].ItemId`, item.itemId);
          if (item.unitPrice != null) formData.append(`Items[${index}].UnitPrice`, item.unitPrice);
          if (item.discountType != null) formData.append(`Items[${index}].DiscountType`, item.discountType);
          if (item.discountValue != null) formData.append(`Items[${index}].DiscountValue`, item.discountValue);
          if (item.discountAmount != null) formData.append(`Items[${index}].DiscountAmount`, item.discountAmount);
          if (item.cgstRate != null) formData.append(`Items[${index}].CGSTRate`, item.cgstRate);
          if (item.sgstRate != null) formData.append(`Items[${index}].SGSTRate`, item.sgstRate);
          if (item.igstRate != null) formData.append(`Items[${index}].IGSTRate`, item.igstRate);
        }
      });

      // Add document IDs to delete
      if (payload.documentIdsToDelete?.length > 0) {
        payload.documentIdsToDelete.forEach((docId, index) => {
          formData.append(`DocumentIdsToDelete[${index}]`, docId);
        });
      }

      // Add new documents
      if (payload.documents?.length > 0) {
        payload.documents.forEach((file) => {
          formData.append("Documents", file);
        });
      }

      const { data } = await axios.put(API_URL.SALES_RETURN.SLUG, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Refresh the detail after update
      dispatch(getSalesReturnById(payload.salesReturnId));

      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const downloadSalesReturnDocument = createAsyncThunk(
  "salesReturn/downloadDocument",
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_URL.SALES_RETURN.DOWNLOAD_DOCUMENT(documentId),
        { responseType: "blob" }
      );

      // Extract content type and filename from response headers
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const contentDisposition = response.headers["content-disposition"];
      let filename = "document";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }

      // Create download link with correct MIME type
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Prepare data for Sales Return approval
// Returns header, items with bin selection options, and write-off capabilities
export const prepareApproval = createAsyncThunk(
  "salesReturn/prepareApproval",
  async (salesReturnId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SALES_RETURN.PREPARE_APPROVAL(salesReturnId)
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Approve Sales Return with items (for Physical Return with bin selection and write-off)
export const approveSalesReturnWithItems = createAsyncThunk(
  "salesReturn/approveSalesReturnWithItems",
  async ({ salesReturnId, items }, { rejectWithValue, dispatch }) => {
    try {
      // Build request body matching SalesReturnApproveRequest DTO
      const requestBody = {
        items: items.map((item) => ({
          salesReturnItemId: item.salesReturnItemId,
          targetBinId: item.targetBinId || null,
          writeOffQty: item.writeOffQty || 0,
          writeOffReason: item.writeOffReason || null,
        })),
      };

      const { data } = await axios.post(
        API_URL.SALES_RETURN.APPROVE(salesReturnId),
        requestBody
      );

      // Refresh the detail after approval
      dispatch(getSalesReturnById(salesReturnId));

      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  sales_return_list: [],
  sales_return_detail: null,
  sales_return_items: [],
  current_values: null,
  active_payload: null,
  total_records: 0,
  isLoading: false,
  // Create flow state
  eligible_invoices: [],
  eligible_invoices_loading: false,
  prepare_data: null,
  prepare_loading: false,
  // Approval flow state
  approval_data: null,
  approval_loading: false,
};

const salesReturnSlice = createSlice({
  name: "salesReturn",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.sales_return_list = [];
    },
    resetSalesReturnDetail(state) {
      state.sales_return_detail = null;
      state.sales_return_items = [];
    },
    resetEligibleInvoices(state) {
      state.eligible_invoices = [];
      state.eligible_invoices_loading = false;
    },
    resetPrepareData(state) {
      state.prepare_data = null;
      state.prepare_loading = false;
    },
    resetCreateState(state) {
      state.eligible_invoices = [];
      state.eligible_invoices_loading = false;
      state.prepare_data = null;
      state.prepare_loading = false;
    },
    resetApprovalData(state) {
      state.approval_data = null;
      state.approval_loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSalesReturns.fulfilled, (state, action) => {
        state.sales_return_list = action.payload.sales_return_list;
        state.total_records = action.payload.data.totalRecords || 0;
      })
      .addCase(getSalesReturnById.fulfilled, (state, action) => {
        state.sales_return_detail = action.payload;
      })
      .addCase(getSalesReturnItems.fulfilled, (state, action) => {
        state.sales_return_items = action.payload;
      })
      // Eligible invoices
      .addCase(getEligibleInvoices.pending, (state) => {
        state.eligible_invoices_loading = true;
      })
      .addCase(getEligibleInvoices.fulfilled, (state, action) => {
        state.eligible_invoices = action.payload?.invoices || [];
        state.eligible_invoices_loading = false;
      })
      .addCase(getEligibleInvoices.rejected, (state) => {
        state.eligible_invoices = [];
        state.eligible_invoices_loading = false;
      })
      // Prepare sales return
      .addCase(prepareSalesReturn.pending, (state) => {
        state.prepare_loading = true;
      })
      .addCase(prepareSalesReturn.fulfilled, (state, action) => {
        state.prepare_data = action.payload;
        state.prepare_loading = false;
      })
      .addCase(prepareSalesReturn.rejected, (state) => {
        state.prepare_data = null;
        state.prepare_loading = false;
      })
      // Prepare approval
      .addCase(prepareApproval.pending, (state) => {
        state.approval_loading = true;
      })
      .addCase(prepareApproval.fulfilled, (state, action) => {
        state.approval_data = action.payload;
        state.approval_loading = false;
      })
      .addCase(prepareApproval.rejected, (state) => {
        state.approval_data = null;
        state.approval_loading = false;
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  resetCurrentValue,
  resetSalesReturnDetail,
  resetEligibleInvoices,
  resetPrepareData,
  resetCreateState,
  resetApprovalData,
} = salesReturnSlice.actions;
export default salesReturnSlice.reducer;
